import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, ArrowRightCircle, Search, Ban } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatPrice, formatDate } from "@/lib/api";
import { Th, Td } from "@/components/admin/shared/UIPrimitives";

const REASON_LABELS = {
  "no-show": "No-show",
  "class-canceled": "Class canceled",
  "goodwill": "Goodwill",
  "make-up": "Make-up",
  "refund-as-credit": "Refund as credit",
};

const STATUS_STYLES = {
  available: "bg-gg-green/15 text-gg-green",
  applied: "bg-gg-brown/15 text-gg-brown",
  expired: "bg-gg-muted/15 text-gg-muted",
  revoked: "bg-[#B24C4C]/15 text-[#B24C4C]",
};

export default function CreditsTab() {
  const [statusFilter, setStatusFilter] = useState("available");
  const [emailFilter, setEmailFilter] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [applying, setApplying] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(null);

  const { data, refetch } = useQuery({
    queryKey: ["adminCredits", statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      return (await api.get(`/admin/credits?${params.toString()}`)).data;
    },
  });
  const credits = data?.credits || [];
  const totalAvailable = data?.total_available || 0;
  const counts = data?.counts || {};

  const filtered = useMemo(() => {
    if (!emailFilter.trim()) return credits;
    const q = emailFilter.trim().toLowerCase();
    return credits.filter((c) =>
      c.parent_email.includes(q) || c.parent_name.toLowerCase().includes(q) ||
      c.student_name.toLowerCase().includes(q)
    );
  }, [credits, emailFilter]);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3">
        <StatCard label="Available balance" value={formatPrice(totalAvailable)} accent="green" testId="credits-total-available" />
        <StatCard label="Available credits" value={counts.available || 0} />
        <StatCard label="Applied credits" value={counts.applied || 0} />
        <StatCard label="Expired / revoked" value={(counts.expired || 0) + (counts.revoked || 0)} accent="muted" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-gg-bg border border-gg-border rounded-full p-1">
          {[
            ["available", "Available"],
            ["applied", "Applied"],
            ["expired", "Expired"],
            ["revoked", "Revoked"],
            ["", "All"],
          ].map(([v, l]) => (
            <button
              key={v || "all"}
              type="button"
              onClick={() => setStatusFilter(v)}
              className={`text-xs px-3 py-1 rounded-full transition-colors ${statusFilter === v ? "bg-gg-green text-white" : "text-gg-brown hover:text-gg-green"}`}
              data-testid={`credits-filter-${v || "all"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gg-muted" />
          <Input
            placeholder="Search by parent email, name, or student"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            className="pl-9"
            data-testid="credits-search"
          />
        </div>
        <button
          type="button"
          onClick={() => setIssuing(true)}
          className="btn-pill btn-primary text-sm"
          data-testid="credits-issue-btn"
        >
          <Plus size={14} /> Issue credit
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-gg-border bg-gg-surface p-8 text-center text-gg-muted">
          No credits match the current filter.
        </div>
      ) : (
        <div className="rounded-2xl border border-gg-border bg-gg-surface overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gg-bg text-xs uppercase tracking-[0.14em] text-gg-brown">
                <tr>
                  <Th>Parent / Student</Th>
                  <Th>Balance</Th>
                  <Th>Reason</Th>
                  <Th>From class</Th>
                  <Th>Status</Th>
                  <Th>Issued / Expires</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-t border-gg-border align-top" data-testid={`credit-row-${c.id}`}>
                    <Td>
                      <div className="font-semibold">{c.parent_name}</div>
                      <div className="text-xs text-gg-muted">{c.parent_email}</div>
                      <div className="text-xs text-gg-brown mt-1">Student: {c.student_name}</div>
                    </Td>
                    <Td>
                      <div className="font-heading text-lg text-gg-green">{formatPrice(c.amount_remaining)}</div>
                      {c.amount_remaining !== c.amount && (
                        <div className="text-[11px] text-gg-muted">of {formatPrice(c.amount)}</div>
                      )}
                    </Td>
                    <Td className="text-xs">{REASON_LABELS[c.reason] || c.reason}</Td>
                    <Td className="text-xs max-w-[200px]">
                      {c.origin_class_title ? (
                        <>
                          <div className="line-clamp-2">{c.origin_class_title}</div>
                          {c.origin_class_date && (
                            <div className="text-gg-muted mt-0.5">{formatDate(c.origin_class_date)}</div>
                          )}
                        </>
                      ) : <span className="text-gg-muted">—</span>}
                    </Td>
                    <Td>
                      <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_STYLES[c.status] || ""}`}>
                        {c.status}
                      </span>
                    </Td>
                    <Td className="text-xs text-gg-muted">
                      <div>{formatDate(c.issued_at)}</div>
                      {c.expires_at && (
                        <div className={new Date(c.expires_at) < new Date() ? "text-[#B24C4C] mt-0.5" : "mt-0.5"}>
                          exp {formatDate(c.expires_at)}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          onClick={() => setViewingHistory(c)}
                          className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green hover:text-gg-green"
                          data-testid={`credit-history-${c.id}`}
                        >
                          History ({c.applications?.length || 0})
                        </button>
                        {c.status === "available" && c.amount_remaining > 0 && (
                          <button
                            type="button"
                            onClick={() => setApplying(c)}
                            className="text-xs px-3 py-1 rounded-full bg-gg-green/10 text-gg-green hover:bg-gg-green hover:text-white transition-colors"
                            data-testid={`credit-apply-${c.id}`}
                          >
                            <ArrowRightCircle size={11} className="inline" /> Apply
                          </button>
                        )}
                        {c.status === "available" && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm(`Revoke this ${formatPrice(c.amount_remaining)} credit for ${c.parent_name}? This cannot be undone.`)) return;
                              try {
                                await api.post(`/admin/credits/${c.id}/revoke`);
                                toast.success("Credit revoked");
                                refetch();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Revoke failed");
                              }
                            }}
                            className="text-xs px-3 py-1 rounded-full bg-gg-bg border border-gg-border text-gg-muted hover:text-[#B24C4C] hover:border-[#B24C4C]"
                            data-testid={`credit-revoke-${c.id}`}
                          >
                            <Ban size={11} className="inline" /> Revoke
                          </button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {issuing && (
        <IssueCreditDialog
          onClose={() => setIssuing(false)}
          onSaved={() => { setIssuing(false); refetch(); }}
        />
      )}
      {applying && (
        <ApplyCreditDialog
          credit={applying}
          onClose={() => setApplying(null)}
          onSaved={() => { setApplying(null); refetch(); }}
        />
      )}
      {viewingHistory && (
        <CreditHistoryDialog
          credit={viewingHistory}
          onClose={() => setViewingHistory(null)}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  const color = accent === "green" ? "text-gg-green" : accent === "muted" ? "text-gg-muted" : "text-[var(--gg-text)]";
  return (
    <div className="rounded-2xl border border-gg-border bg-gg-surface p-5" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.18em] text-gg-brown font-semibold">{label}</p>
      <p className={`font-heading text-4xl mt-2 leading-none ${color}`}>{value}</p>
    </div>
  );
}

function IssueCreditDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    parent_email: "", parent_name: "", student_name: "",
    amount: "", reason: "no-show",
    origin_class_title: "", origin_class_date: "",
    expires_in_months: 12, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const save = async () => {
    if (!form.parent_email || !form.parent_name || !form.student_name || !form.amount) {
      toast.error("Parent email/name, student name, and amount are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/credits", {
        ...form,
        amount: Number(form.amount),
        expires_in_months: form.expires_in_months ? Number(form.expires_in_months) : 0,
      });
      toast.success("Credit issued");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue credit");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Issue a credit</DialogTitle>
          <DialogDescription>
            Track a credit for a parent so it can be applied to a future class. All credits are logged and traceable.
          </DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Parent email *</Label>
            <Input type="email" value={form.parent_email} onChange={setF("parent_email")} className="mt-1.5" data-testid="issue-credit-email" />
          </div>
          <div>
            <Label>Parent name *</Label>
            <Input value={form.parent_name} onChange={setF("parent_name")} className="mt-1.5" data-testid="issue-credit-parent-name" />
          </div>
          <div>
            <Label>Student name *</Label>
            <Input value={form.student_name} onChange={setF("student_name")} className="mt-1.5" data-testid="issue-credit-student-name" />
          </div>
          <div>
            <Label>Amount ($) *</Label>
            <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={setF("amount")} className="mt-1.5" data-testid="issue-credit-amount" />
          </div>
          <div>
            <Label>Reason</Label>
            <select
              value={form.reason}
              onChange={setF("reason")}
              className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
              data-testid="issue-credit-reason"
            >
              {Object.entries(REASON_LABELS).map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
            </select>
          </div>
          <div>
            <Label>Origin class title (optional)</Label>
            <Input value={form.origin_class_title} onChange={setF("origin_class_title")} className="mt-1.5" placeholder="e.g. Field Trip: Homestead Basics" />
          </div>
          <div>
            <Label>Origin class date (optional)</Label>
            <Input type="date" value={form.origin_class_date} onChange={setF("origin_class_date")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Expires in (months, 0 = never)</Label>
            <Input type="number" min="0" value={form.expires_in_months} onChange={setF("expires_in_months")} className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes (private)</Label>
            <Textarea rows={2} value={form.notes} onChange={setF("notes")} className="mt-1.5" placeholder="Why was this credit issued? Any context…" data-testid="issue-credit-notes" />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="issue-credit-save">
            {busy ? "Issuing…" : "Issue credit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplyCreditDialog({ credit, onClose, onSaved }) {
  const [regId, setRegId] = useState("");
  const [amount, setAmount] = useState(credit.amount_remaining);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Fetch registrations for this parent (unpaid preferred but we show all)
  const { data: regs = [] } = useQuery({
    queryKey: ["parentRegistrationsForCredit", credit.parent_email],
    queryFn: async () => (await api.get("/admin/registrations")).data,
  });
  const parentRegs = regs.filter(
    (r) => (r.parent_email || "").toLowerCase() === credit.parent_email.toLowerCase()
  );

  const apply = async () => {
    if (!regId) { toast.error("Pick a registration to apply this credit to"); return; }
    const amt = Number(amount);
    if (amt <= 0) { toast.error("Amount must be positive"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/credits/${credit.id}/apply`, {
        registration_id: regId, amount: amt, notes,
      });
      toast.success(`Applied ${formatPrice(amt)} to registration`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Apply credit</DialogTitle>
          <DialogDescription>
            Applying <strong>{formatPrice(credit.amount_remaining)}</strong> credit for {credit.parent_name} ({credit.parent_email}). The registration&apos;s price will be reduced so the invoice reflects the credit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Pick a registration</Label>
            {parentRegs.length === 0 ? (
              <div className="mt-2 rounded-lg border border-gg-border bg-gg-bg p-4 text-sm text-gg-muted">
                No registrations found for <strong>{credit.parent_email}</strong>. Register the student for a class first, then come back here to apply this credit.
              </div>
            ) : (
              <select
                value={regId}
                onChange={(e) => setRegId(e.target.value)}
                className="mt-1.5 w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm"
                data-testid="apply-credit-reg-select"
              >
                <option value="">— Pick a registration —</option>
                {parentRegs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.student_name} · {r.class_title} · {formatPrice(r.price_student)}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <Label>Amount to apply</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={credit.amount_remaining}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1.5"
              data-testid="apply-credit-amount"
            />
            <p className="text-xs text-gg-muted mt-1">
              Max available: {formatPrice(credit.amount_remaining)}. Applying less than the full amount leaves the remainder on the credit for later.
            </p>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" placeholder="e.g. Applied per parent's request to reschedule…" />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
          <button type="button" onClick={apply} disabled={busy || parentRegs.length === 0} className="btn-pill btn-primary disabled:opacity-60" data-testid="apply-credit-save">
            {busy ? "Applying…" : "Apply credit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreditHistoryDialog({ credit, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Credit history</DialogTitle>
          <DialogDescription>
            {credit.parent_name} · {formatPrice(credit.amount)} · issued {formatDate(credit.issued_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-lg border border-gg-border bg-gg-bg p-4 space-y-1">
            <div><strong>Reason:</strong> {REASON_LABELS[credit.reason] || credit.reason}</div>
            <div><strong>Issued by:</strong> {credit.issued_by_admin}</div>
            {credit.origin_class_title && <div><strong>From class:</strong> {credit.origin_class_title} ({formatDate(credit.origin_class_date)})</div>}
            {credit.expires_at && <div><strong>Expires:</strong> {formatDate(credit.expires_at)}</div>}
            {credit.notes && <div className="italic text-gg-muted mt-2">&ldquo;{credit.notes}&rdquo;</div>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">
              Applications ({credit.applications?.length || 0})
            </p>
            {!credit.applications || credit.applications.length === 0 ? (
              <p className="text-gg-muted italic">Not yet applied to any class.</p>
            ) : (
              <ul className="space-y-2">
                {credit.applications.map((a, i) => (
                  <li key={i} className="rounded-lg border border-gg-border bg-gg-surface p-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{a.class_title}</span>
                      <span className="text-gg-green">{formatPrice(a.amount)}</span>
                    </div>
                    <div className="text-xs text-gg-muted mt-1">
                      For {a.student_name} · {a.class_date ? formatDate(a.class_date) : ""} · applied by {a.applied_by_admin} on {formatDate(a.applied_at)}
                    </div>
                    {a.notes && <div className="text-xs italic text-gg-muted mt-1">&ldquo;{a.notes}&rdquo;</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-pill btn-primary">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
